// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {IUser} from './user';
import {IGroup} from './group';
import {
    IDeviceConfigEncryptedDirectory,
    IKnownGoodApp,
    IPbaUser,
    IPbaEnrollment,
    IFdeUser,
    IFdeEnrollment,
    ISensor,
    PolicyCompliance, ransomwareFeatureMaskValues,
    validReAuthFrequencyValues,
    ISelfEnrollment,
    IPbaLicenseInfo,
    IFdeLicenseInfo
} from './d3ePolicy';
import {TranslateService} from '@ngx-translate/core';
import {BitLockerCompliance} from './bitlocker-compliance';
import { IDownloadVersion } from '../app/dialogs/news/news.component';

export const TPM_MIN_VERSION = '1.2';
export const MIN_BITLOCKER_VERSION = '4.1.0';
export const MIN_BITLOCKER_WINDOWS_VERSION = '10.0.10586'; // a.k.a.: Windows 10 (1511)

export interface ISubscriptionRequest {
    subscriptionId: string;
    tokenId?: string;
    planId: string;
    newPlanId?: string;
    licenseQuantity: number;
    email: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    country?: string;
    zip?: string;
    trialCode?: string;
    couponCode?: string;
    type?: LicenseType;
}

export interface ISubscriptions {
    subscriptions: ISubscription[];
}

export interface ISubscription {
    id: number;
    user_id?: number;   // TODO: Remove completely
    company?: string;
    org_type?: number;
    email?: string;
    plan_id: string;
    subscription_id: string;
    group_id: number;
    description: string;
    sub_timestamp: string;
    exp_timestamp: string;
    maint_exp_timestamp?: string;
    max_duration_days?: number;
    status: SubscriptionStatus;
    type: LicenseType;
    count: number;
    used: number;
    pba_used?: number;
    fde_used?: number;
    features: string;
    tier: D3ETier;
    stripe_product: IStripeProduct;
    stripe_plan: IStripePlan;
    recurring: number;
    uuid: string;
    display_expiration_date?: number;
    display_expiration_warning?: number;
    expiration_warning_interval?: number;
    grace_period?: number;
    display_expired_warning?: number;
    expired_warning_interval?: number;
    trial_period_days: number;
    enable_pba_extended_features?: boolean;
    enable_pba_log_erase?: boolean;
}

export interface ISubAllocationResponse {
    subscription: ISubscription;
}

export interface ISubscriptionResult {
    subscription: ISubscription;
    software_versions: ISoftwareVersion;
}

export interface IPolicyCompliance {
    protectedDirs: PolicyCompliance;
    lockedDirs: PolicyCompliance;
    protectedExts: PolicyCompliance;
    lockedExts: PolicyCompliance;
    encryptedProtectedDirs: PolicyCompliance;
    encryptedLockedDirs: PolicyCompliance;
    deceptionFiles: PolicyCompliance;
    syncInterval: PolicyCompliance;
    syncIntervalMetered: PolicyCompliance;
    knownGood: PolicyCompliance;
    preAuthFileAccessCount: PolicyCompliance;
    preAuthFileAccessTimeout: PolicyCompliance;
    forcePreAuthValues: PolicyCompliance;
    ransomwareSensitivity: PolicyCompliance;
    ransomwareFeatureMask: PolicyCompliance;
    ransomExts: PolicyCompliance;
    sensorConfigs: PolicyCompliance;
    volumeConfigs: PolicyCompliance;
    hklm_software_policies_microsoft_fve: PolicyCompliance;
    pbaFailedLoginsBeforeLockout: PolicyCompliance;
    pbaFailedLoginsBeforeErase: PolicyCompliance;
    pbaPasswordHistory: PolicyCompliance;
    pbaPasswordMinLength: PolicyCompliance;
    pbaRequireMFA: PolicyCompliance;
    pbaMFAOptions: PolicyCompliance;
    pbaEnableRememberMe: PolicyCompliance;
    pbaAllowTpmForAutoLogin: PolicyCompliance;
    pbaSmartcardLoginEnabled: PolicyCompliance;
    pbaSecurityKeyLoginEnabled: PolicyCompliance;
    fdeFailedLoginsBeforeLockout: PolicyCompliance;
    fdeFailedLoginsBeforeErase: PolicyCompliance;
    fdePasswordHistory: PolicyCompliance;
    fdePasswordMinLength: PolicyCompliance;
    fdeEnableRememberMe: PolicyCompliance;
}

export interface IPbaSettings {
    enable: boolean;
    enable_remember_me: boolean;
    failed_login_before_erase: number;
    failed_login_before_lockout: number;
    last_event: number;
    mfa_required: number;
    password_history: number;
    password_min: number;
    security_key_login_enabled: boolean;
    smartcard_login_enabled: boolean;
    tpm_auto: boolean;
}

// include some subscription data
export interface IDevice extends ILicense {
    subscription_id: string;
    sub_timestamp: string; // assuming this is create time
    exp_timestamp: string; // assuming this changes
    // max_duration_days?: number;
    enable_lock_action: boolean;
    pending?: number;
    version: string;
    opal_enabled?: boolean;
    volumes?: number;
    folder_enabled?: boolean;
    locked_folders?: number;
    protected_folders?: number;
    deception_enabled?: boolean;
    deception_files?: number;
    ext_enabled?: boolean;
    locked_exts?: number;
    protected_exts?: number;
    state_tooltip?: string;
    toggle: boolean;
    user?: IUser;   //  TODO: Remove Me, IDevices[] only returns last_logged_in_user and org
    org?: IGroup;   // TODO: calls that return IDevice should also return org, or consolidate with below values, and add partner_id
    organizationName?: string;
    PrimaryEmail?: string;
    subscriptionName?: string;
    feature_flags_sent?: number;
    force_sync?: 0 | 1;
    force_policy_reset: 0 | 1;
    bitlocker_compliance?: BitLockerCompliance;
    overall_policy_compliance?: PolicyCompliance | null;
    overall_licensing_state?: string;
    policy_compliance: IPolicyCompliance | null;
    /**
    * lock state has the following values
    * 0 unlocked and not pending (lock is unlocked and grey)
    * 1 unlocked and pending ( locke is unlocked and orange)
    * 2 locked and not pending ( lock is locked and blue)
    * 3 locked and pending ( lock is locked and orange )
    */
    lock_state: number;
    pending_list?: IPendingList;
    volume_config_guids?: string[];
    pbaSettings?: IPbaSettings;
    db_pba_users?: IPbaUser[];
    db_pba_enrollment?: IPbaEnrollment[];
    db_fde_users?: IFdeUser[];
    db_fde_enrollment?: IFdeEnrollment[];
    db_pending_pba_users?: IPbaUser[];
    db_pending_pba_enrollment?: IPbaEnrollment[];
    has_pending_pba_remove_all?: boolean;
    db_pending_fde_users?: IFdeUser[];
    db_pending_fde_enrollment?: IFdeEnrollment[];
    has_pending_fde_remove_all?: boolean;
    db_pba_license_info?: IPbaLicenseInfo[];
    db_fde_license_info?: IFdeLicenseInfo;
    pbaAuthorization?: boolean;
    fdeAuthorization?: boolean;
}

export const MaxEndpointPageSize = 1000;

export interface IEndpointExportToken {
    token: string;
}

export interface IEndpoint extends IDevice {
    manualLock: string;
    activeLock: string;
    os: string;
    device_set_id: number;
    device_set_name: string;
    policy_id: number;
    policy_name: string;
    subscription_id: string;
    lat?: number;
    lon?: number;

    // Protection
    secure_drive_count?: number;
    folder_count?: number;
    file_type_count?: number;
    pba_count?: number;
    fde_count?: number;
    agent_count?: number;
    // Encryption
    protectors?: IVolumeProtection[];
    protectorDisplay?: string;
    os_volume_encrypted?: boolean;
    os_encryption?: VolumeEncryptionType;
    fixed_drives_encrypted?: boolean;
    fixed_drives_encryption?: VolumeEncryptionType[]; // multiple drives, manual configs may make them differ
    fixedDrivesEncryptionDisplay?: string;
    bitlocker_key_available?: boolean;
    os_version?: string;
    tpm_version?: string;
}

export interface IPendingList {
    pending: IPending[];
}

export interface IPending {
    sensor_id: number;
    enabled: boolean;
}

export interface IDeviceConfig {
    sys: string;
    man: string;
    mod: string;
    cmp: string;
    os_serial: string;
    syncInterval?: number;
    syncIntervalMetered?: number;
    licenseInterval?: number;
    licenseIntervalMetered?: number;
    forcePreAuthValues?: validReAuthFrequencyValues;
    preAuthFileAccessCount?: number;
    preAuthFileAccessTimeout?: number;
    ransomwareSensitivityMin?: number;
    ransomwareSensitivityMax?: number;
    ransomwareFeatureMask?: ransomwareFeatureMaskValues;
    version: IVersion;
    clientName: string;

    disks: IDisk[];
    networks: INetwork[];
    
    // New Policy has to store disabled values, ignored everywhere else
    disabledDirs?: string[];
    overrideLocalPolicy?: boolean;
    allowAlwaysOn?: boolean;

    // Spec 30
    protectedDirs?: string[];
    encryptedProtectedDirs?: IDeviceConfigEncryptedDirectory[];
    lockedDirs?: string[];
    encryptedLockedDirs?: IDeviceConfigEncryptedDirectory[];

    // New Policy has to store disabled values, ignored everywhere else
    disabledExts?: string[];

    protectedExts?: string[];
    lockedExts?: string[];

    protectedVolumes?: string[];
    lockedVolumes?: string[];

    ransomExts?: string[];
    deceptionFiles?: string[];
    deceptionUrls?: string[];

    // Spec 22
    protected?: string[];
    locked?: string[];
    deceptions?: string[];

    configuredAuthenticationModes: number[];

    os_version?: string;
    knownGood?: IKnownGoodApp[];

    sensorConfigs?: ISensor[];

    hklm_software_policies_microsoft_fve?: {[key: string]: string};
    tpm?: string;
}

export interface IDisk {
    configMode?: number;
    man: string;
    mod: string;
    opal_state: number;
    disk_config_state?: number;
    size: number;
    tcg_support?: string[];
    type: string;
    volumes: IVolume[];
    fw?: string;
    support?: string;
    serial?: string;
    fips?: number;
    removable?: boolean;
    pba_info?: IPBAInfo | null;
    db_pba_users?: IPbaUser[]; // database
    db_pba_self_enrollment?: ISelfEnrollment[]; // database
    ransomwareRawSensitivity?: number;
    license_version?: string;
    last_config: number;
}

export interface IConfigPbaSettings {
  serial: string;
  failed_logins_before_lockout: number;
  failed_logins_before_erase: number;
  password_history: number;
  password_min: number;
  mfa_required: number;
  mfa_option: number;
  enable_remember_me: boolean;
  tpm_auto: boolean;
  smartcard_login_enabled: boolean;
  security_key_login_enabled: boolean; 
}

export interface IFdeSettings {
  failed_logins_before_lockout: number;
  failed_logins_before_erase: number;
  password_history: number;
  password_min: number;
  enable_remember_me: boolean;
}

export interface IPBAInfo {
    installed: boolean;
    mask: number;
    enabled?: boolean;
    version?: string;
    pba_brand?: number;
    last_event?: number;
    settings?: IConfigPbaSettings;
    users?: IPbaUser[];
    enrollment_codes?: ISelfEnrollment[];
}

export interface INetwork {
    name: string;
    online: boolean;
    onlineCount: number;
    offlineCount: number;
    blockedCount: number;
    firstConnected: number;
    ipv4Address: string;
    gateway_mac: string;
    network_state: number;
    metered: boolean;
}

export interface IVolume {
    guid: string;
    label: string;
    mount: string;
    volume_type?: VolumeType;
    filesystem?: string;
    diskSerial?: string;
    size?: number;
    locked?: number;
    protection?: VolumeProtection;
    volume_encryption?: IVolumeEncryption;
}

export interface IVolumeEncryption {
    encryption?: string;  // currently only BitLocker
    protectors?: [IVolumeProtection];
    protector_display?: string;
    encryption_type?: VolumeEncryptionType;
    encryption_flags?: IVolumeEncryptionFlags;
    encrypt_status?: VolumeStatus;
    percent_encrypted?: number;
    wipe_status?: VolumeWipeStatus;
    percent_wiped?: number;
    key_id?: string;  // This value is derived from the protectors, populated by key for
    key_available?: boolean;
    users?: IPbaUser[];
    enrollment_codes?: ISelfEnrollment[];
    enabled?: boolean;
    version?: string;
    serial?: string;
    settings?: IFdeSettings | null;
}

export interface IVolumeProtection {
    protector_type: VolumeProtectorType;
    key_id: string;
}

export enum VolumeWipeStatus {
    FREE_SPACE_NOT_WIPED = 0,
    FREE_SPACE_WIPED = 1,
    FREE_SPACE_WIPING_IN_PROGRESS = 2,
    FREE_SPACE_WIPING_PAUSED = 3
}

export enum VolumeProtectorType {
    OTHER = 0,
    TPM = 1,
    EXTERNAL_KEY = 2,
    NUMERICAL_PASSWORD = 3, // the only one for which we care about the key_id
    PROTECT_KEY_WITH_TPM_AND_PIN = 4,
    TPM_AND_STARTUP_KEY = 5,
    TPM_AND_PIN_AND_STARTUP_KEY = 6,
    PUBLIC_KEY = 7,
    PASSPHRASE = 8,
    TPM_CERTIFICATE = 9,
    SECURITY_IDENTIFIER = 10
}

export enum IVolumeEncryptionFlags {
    DATA_ONLY_MODE = 1,
    MAY_WIPE_FREE_SPACE = 2,
    SYNCHRONOUS = 65536 // 0x00010000
}

export enum VolumeEncryptionType {
    NOT_ENCRYPTED = 0,
    AES128_DIFFUSER = 1,
    AES256_DIFFUSER = 2,
    AES128 = 3,
    AES256 = 4,
    HARDWARE = 5,
    XTSAES128 = 6,
    XTSAES256_DIFFUSER = 7
}

export enum VolumeType {
    OPERATING_SYSTEM_VOLUME = 0,
    FIXED_DATA_VOLUME = 1,
    REMOVABLE_DATA_VOLUME = 2,
    OTHER = 3
}

export enum VolumeProtection {
    UNPROTECTED = 0,
    PROTECTED = 1,
    UNKNOWN = 2
}


export enum VolumeStatus {
    FULLY_DECRYPTED = 0,
    FULLY_ENCRYPTED = 1,
    ENCRYPTION_IN_PROGRESS = 2,
    DECRYPTION_IN_PROGRESS = 3,
    ENCRYPTION_PAUSED = 4,
    DECRYPTION_PAUSED = 5
}

export interface IDeviceState {
    activeLock: number[];
    activeNetwork: object[];
    allowDeactivate: boolean;
    authenticationMode: number;
    enabled: boolean;
    inetBlock: boolean;
    netcomBlock: boolean;
}

export interface IVersion {
    major: number;
    minor: number;
    revision: number;
    build?: string;
}

export interface ILicense {
    id: number;
    uuid: string;
    sub_id: number;
    user_id: number;
    group_id: number;
    token: string;
    agent_version: string;
    bios_serial: string;
    system_serial: string;
    manufacturer: string;
    model: string;
    computer_name: string;
    created_dttm: string;
    deactivated: number;
    deactivated_dttm: string;
    version: string;
    config: IDeviceConfig;
    os_serial: string;
    last_check_dttm: string;
    active_lock?: number;
    enabled?: number;
    state: IDeviceState;
    last_config_dttm?: string;
    last_license_dttm?: string;
    last_state_dttm?: string;
    last_threats_dttm?: string;
    obj_version?: number;
    threats_pending: number; // values of 0, 1, 2 (clear, pending, waiting for ack)
    last_seen_seconds?: number;  // can be calculated with LicenseMethods.setLastSeen()
    last_seen?: string;
    last_logged_in_user_id: number;
    last_logged_in_user?: IUser;
    last_logged_in_time: string;
}

export interface IActivationToken {
    id: number;
    group_id: number | null;
    expiration_timestamp: string; 
    activated: number;
    system_serial: string;
    manufacturer: string;
    model: string;
    computer_name: string;
}

export interface ISoftwareVersion {
    // id: number;
    // created_timestamp: string;
    major: number;
    minor: number;
    revision: number;
    path: string;
    sha256sum?: string;
    system?: string;
    feature_flags?: string;
    activation_code?: string;
    partner_id?: number | null;
    sub_uuid?: string | null;
    installer_name?: string;
    subscription_path?: string;
    description?: string;
    detail?: string;
    created_timestamp?: string;
    rest_api_version?: {
        major: number;
        minor: number;
        revision: number;
        date?: string;
    };
    msi_url?: string | null;
    msi_version?: string | null;
    pba_versions?: IDownloadVersion[] | null;
    win_fde_versions?: IDownloadVersion[] | null;
    ubuntu_fde_versions?: IDownloadVersion[] | null;
    rhel_fde_versions?: IDownloadVersion[] | null;
    win_cli_versions?: IDownloadVersion[] | null;
    ubuntu_cli_versions?: IDownloadVersion[] | null;
    rhel_cli_versions?: IDownloadVersion[] | null;
    win_agent_versions?: IDownloadVersion[] | null;
    ubuntu_agent_versions?: IDownloadVersion[] | null;
    rhel_agent_versions?: IDownloadVersion[] | null;
    cli_guide_url?: string | null;
    cli_guide_version?: string | null;
}


export enum SubscriptionStatus {
    SUBSCRIPTION_STATUS_VALID = 0,
    SUBSCRIPTION_STATUS_EXPIRED = 1,
}

export enum LicenseType {
    LICENSE_TYPE_INVALID = 0,
    LICENSE_TYPE_FREE = 1,
    LICENSE_TYPE_PERPETUAL = 2,
    LICENSE_TYPE_TIME_LIMITED = 3,
    LICENSE_TYPE_OFFLINE = 4,
    LICENSE_TYPE_TRIAL = 5,
}

export interface ISelectableType {
    label: string;
    value: number;
}
export const selectableTypes: ISelectableType[] = [
    // { label: 'free',
    //   value: LicenseType.LICENSE_TYPE_FREE },
    { label: 'trial',
      value: LicenseType.LICENSE_TYPE_TRIAL },
    { label: 'time_limited',
      value: LicenseType.LICENSE_TYPE_TIME_LIMITED }
  ];

export enum D3ETier {
    INVALID = 0,
    VAULT = 1,
    LIGHT = 2,
    FULL = 3,
    ENTERPRISE = 4,
    MONITOR = 5,
    LIGHT_PLUS_TYPING = 6,
    LIGHT_PLUS_SENT_ONE = 7,
    LIGHT_PLUS_SENT_ONE_AND_TYPING = 8,
    LIGHT_PLUS_OPAL = 9,
    LIGHT_PLUS_TYPING_AND_OPAL = 10,
    LIGHT_PLUS_SENT_ONE_AND_OPAL = 11,
    LIGHT_PLUS_SENT_ONE_AND_TYPING_AND_OPAL = 12,
}

export type BillingInterval = 
    | 'day'
    | 'week'
    | 'month'
    | 'year';

export const LicenseTierName = {
    INVALID: 'Invalid',
    VAULT: 'D3E Vault',
    LIGHT: 'D3E Personal',
    FULL: 'D3E Premium',
    ENTERPRISE: 'D3E Enterprise',
    MONITOR: 'D3E Monitor',
    LIGHT_PLUS_TYPING: 'LIGHT_PLUS_TYPING',
    LIGHT_PLUS_SENT_ONE: 'LIGHT_PLUS_SENT_ONE',
    LIGHT_PLUS_SENT_ONE_AND_TYPING: 'LIGHT_PLUS_SENT_ONE_AND_TYPING',
    LIGHT_PLUS_OPAL: 'LIGHT_PLUS_OPAL',
    LIGHT_PLUS_TYPING_AND_OPAL: 'LIGHT_PLUS_TYPING_AND_OPAL',
    LIGHT_PLUS_SENT_ONE_AND_OPAL: 'LIGHT_PLUS_SENT_ONE_AND_OPAL',
    LIGHT_PLUS_SENT_ONE_AND_TYPING_AND_OPAL: 'LIGHT_PLUS_SENT_ONE_AND_TYPING_AND_OPAL',
};

export enum StripeProductTier {
    INVALID = 0,
    UNUSED = 1,
    PERSONAL = 2,
    BUSINESS = 3,
    FREE = 4,
    HARDWARE = 5,
    BITLOCKER = 6,
    HARDWARE2 = 7,
    FDE = 8,
    AGENT = 9
}

export const BITLOCKER_PRODUCT_PLAN_ID = "mspmanager_prod_bitlocker";
export const HARDWARE_PRODUCT_PLAN_ID = "mspmanager_prod_hardware";
export const HARDWARE2_PRODUCT_PLAN_ID = "mspmanager_prod_hardware_2";
export const FDE_PRODUCT_PLAN_ID = "mspmanager_prod_fde";
export const AGENT_PRODUCT_PLAN_ID = "mspmanager_prod_agent";

export interface IStripePricingTier {
    id: number;
    stripe_plan_id: number;
    unit_amount: number;
    up_to: number | null;
}

export interface IStripeProduct {
    id: number;
    stripe_product_id: string;
    stripe_type: 'service' | 'good';
    name: string;
    description: string;
    created: string;
    modified: string | null;
    tier: StripeProductTier;
    active: 0 | 1;
    livemode: 0 | 1;
    product_group: string;
    plans: IStripePlan[];
}

export interface IStripePlan {
    id: number;
    stripe_plan_id: string;
    stripe_product_id: string;
    currency: 'USD';
    amount_in_cents: number;
    usage_type: 'licensed'; // 'licensed' | 'metered';
    created: string;
    active: 0 | 1;
    interval_unit: BillingInterval;
    interval_count: number;
    nickname: string;
    billing_scheme: StripeBillingScheme;
    stripe_pricing_tiers: IStripePricingTier[];
    per_group_of: number;
}

export type StripeBillingScheme = 
    | 'per_unit'
    | 'tiered';

export interface IIntegration {
    partnerId: number | null;
    name: string;
    threatIds: number [];
}

export const MIN_AUTH_WIN_NATIVE_VERSION = '3.4.3';

// Matches what's in cyberstack_library/src/libcyberstack/auth.config.h
export enum AuthMode {
    AUTH_INVALID = 0,
    AUTH_FINGER_DEPRECATED,
    AUTH_FACE_DEPRECATED,
    AUTH_GOOGLE,
    AUTH_PASS,
    AUTH_PIN,
    AUTH_EMAIL,
    AUTH_DUO,
    AUTH_PIV,
    AUTH_PIN_RST,
    AUTH_PBA_PW,
    AUTH_PBA_PIV,
    AUTH_PBA_2FA,
    AUTH_MULTI_PROMPT,
    AUTH_WIN_NATIVE
}

export const AuthModeName = {
    AUTH_INVALID: 'Invalid',
    AUTH_FINGER_DEPRECATED: 'Fingerprint',
    AUTH_FACE_DEPRECATED: 'Facial Recognition',
    AUTH_GOOGLE: 'Authenticator App',
    AUTH_PASS: 'Password',
    AUTH_PIN: 'PIN',
    AUTH_EMAIL: 'Email',
    AUTH_DUO: 'Cisco DUO',
    AUTH_PIV: 'CAC/PIV',
    AUTH_WIN_NATIVE: 'Windows Authentication'

};

export function getFullOpalState(opal_state_param, translate): string {
    let opal_state = 3;
    if (opal_state_param !== undefined && opal_state_param != null) {
        opal_state = opal_state_param;
    }
    let opal_state_string: string;

    switch ( opal_state ) {
        case 0:
            opal_state_string = 'opal_state.not_provisioned';
            break;
        case 1:
            opal_state_string = 'opal_state.cigent_provisioned';
            break;
         case 2:
            opal_state_string = 'opal_state.third_party_provisioned';
            break;
         case 3:
            opal_state_string = 'opal_state.unknown';
            break;
         case 4:
            opal_state_string = 'opal_state.supported';
            break;
         case 5:
            opal_state_string = 'opal_state.not_supported';
            break;
         case 6:
            opal_state_string = 'opal_state.sid_blocked';
            break;
         case 7:
            opal_state_string = 'opal_state.secondary_pba';
            break;
        default:
            opal_state_string = 'opal_state.undefined';
            break;
    }

    return translate.instant(opal_state_string, { 'opal_state_value': opal_state});
}


export function getEncryptionTypeDisplayFromVolume(volume: IVolume, translate: TranslateService): string {
    if ( volume.volume_encryption && volume.volume_encryption.encryption_type != undefined ) {
        return getVolumeEncryptionTypeDisplay(volume.volume_encryption.encryption_type, translate);
    }
    return '';
}

export function getVolumeEncryptionTypeDisplay(encryptionType: VolumeEncryptionType, translate: TranslateService): string {
    if ( encryptionType != undefined ) {
        switch ( encryptionType) {
            case VolumeEncryptionType.NOT_ENCRYPTED:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.not_encrypted');
            case VolumeEncryptionType.AES128_DIFFUSER:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.aes128_diffuser');
            case VolumeEncryptionType.AES256_DIFFUSER:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.aes256_diffuser');
            case VolumeEncryptionType.AES128:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.aes128');
            case VolumeEncryptionType.AES256:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.aes256');
            case VolumeEncryptionType.HARDWARE:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.hardware');
            case VolumeEncryptionType.XTSAES128:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.xtsaes128');
            case VolumeEncryptionType.XTSAES256_DIFFUSER:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.xtsaes256_diffuser');
            default:
                return translate.instant('endpoints.partitions.volume_encryption_type_enum.unknown');
        }
    }
    return '';
}

export function getPolicyEncryptionTypeDisplay(encryptionType: EncryptionMethod, translate: TranslateService): string {
    switch (encryptionType) {
        case EncryptionMethod.aesCbc128:
            return translate.instant('endpoints.partitions.volume_encryption_type_enum.aes128');
        case EncryptionMethod.aesCbc256:
            return translate.instant('endpoints.partitions.volume_encryption_type_enum.aes256');
        case EncryptionMethod.xtsAes128:
            return translate.instant('endpoints.partitions.volume_encryption_type_enum.xtsaes128');
        case EncryptionMethod.xtsAes256:
            return translate.instant('endpoints.partitions.volume_encryption_type_enum.xtsaes256_diffuser');
        default:
            return translate.instant('endpoints.partitions.volume_encryption_type_enum.not_encrypted');
    }
}

export function getProtectorDisplayValue(protector: IVolumeProtection, translate: TranslateService): string {
    if ( protector && protector.protector_type != undefined ) {
        switch ( protector.protector_type ) {
            case VolumeProtectorType.TPM:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.TPM');
            case VolumeProtectorType.EXTERNAL_KEY:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.external_key');
            case VolumeProtectorType.NUMERICAL_PASSWORD:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.numerical_password');
            case VolumeProtectorType.PROTECT_KEY_WITH_TPM_AND_PIN:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.protect_key_with_tpm_and_pin');
            case VolumeProtectorType.TPM_AND_STARTUP_KEY:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.tpm_and_startup_key');
            case VolumeProtectorType.TPM_AND_PIN_AND_STARTUP_KEY:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.tpm_and_pin_and_startup_key');
            case VolumeProtectorType.PUBLIC_KEY:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.public_key');
            case VolumeProtectorType.PASSPHRASE:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.passphrase');
            case VolumeProtectorType.TPM_CERTIFICATE:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.tpm_certificate');
            case VolumeProtectorType.SECURITY_IDENTIFIER:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.security_identifier');
            case VolumeProtectorType.OTHER:
            default:
                return translate.instant('endpoints.partitions.volume_protection_type_enum.other');
        }
    }
    return '';
}

export enum RecoveryPackage {
    passwordAndKey,
    passwordOnly,
}

export enum EncryptionType {
    usedSpaceOnly,
    fullEncryption
}

export enum EncryptionMethod {
    aesCbc128,
    aesCbc256,
    xtsAes128,  // Windows 10/11 Default
    xtsAes256,
    notEncrypted,
}

export enum StartupAuthType {
    tpmOnly,
    tpmWithPIN,
    tpmWithStartupKey,
    tpmWithPINAndStartup
}

export enum BitLockerEncryption {
    doNotManage,
    turnOnEncryption,
    turnOffEncryption
}

export enum RecoveryKeyFileCreation {
    allowed,
    disabled
}

export enum RecoveryPasswordFileCreation {
    allowed,
    disabled
}

interface BitlockerDriveConfig {
    manageBitlockerEncryption: BitLockerEncryption;
    recoveryPackage: RecoveryPackage;
    recoveryKeyFileCreation: RecoveryKeyFileCreation;
    recoveryPasswordFileCreation: RecoveryPasswordFileCreation;
    encryptionType: EncryptionType;
    encryptionMethod: EncryptionMethod;
}

interface BitlockerOSDriveConfig extends BitlockerDriveConfig {
    requireStartupAuth: boolean;
    startupAuthType: StartupAuthType;
    allowBitlockerWithoutCompatibleTPM: boolean;
    configurePreBootRecoveryMessage: boolean;
    preBootRecoveryMessage: string;
    preBootRecoveryURL: string;
    minimumPINLength: number;
    allowEnhancedPINSForStartup: boolean;
    disallowStandardUsersChangingAuth: boolean;
}

interface BitlockerFixedDrivesConfig extends BitlockerDriveConfig {
    autoUnlock: boolean;
}

export interface BitlockerPolicySection {
    generalSettings: {
        backupKeyToActiveDirectory: boolean;
        backupKeyToManagementConsole: boolean;
    };
    osDrive: BitlockerOSDriveConfig;
    dataDrives: BitlockerFixedDrivesConfig;
}

const ENCRYPTION_TYPE_STRENGTH: { [key in VolumeEncryptionType]: number } = {
  [VolumeEncryptionType.NOT_ENCRYPTED]: 0,
  [VolumeEncryptionType.AES128]: 1,
  [VolumeEncryptionType.AES256]: 2,
  [VolumeEncryptionType.HARDWARE]: 0,
  [VolumeEncryptionType.XTSAES128]: 4,
  [VolumeEncryptionType.XTSAES256_DIFFUSER]: 5,
  [VolumeEncryptionType.AES128_DIFFUSER]: 1,
  [VolumeEncryptionType.AES256_DIFFUSER]: 2,
};

const ENCRYPTION_METHOD_STRENGTH: { [key in EncryptionMethod]: number } = {
    [EncryptionMethod.notEncrypted]: 0,
    [EncryptionMethod.aesCbc128]: 1,
    [EncryptionMethod.aesCbc256]: 2,
    [EncryptionMethod.xtsAes128]: 4,
    [EncryptionMethod.xtsAes256]: 5,
};

export const getEncryptionStrengthFromVolumeEncryptionType = (encryptionType: VolumeEncryptionType) => {
    const strength = ENCRYPTION_TYPE_STRENGTH[encryptionType];
    if (typeof strength == 'undefined') {
        return ENCRYPTION_TYPE_STRENGTH[VolumeEncryptionType.NOT_ENCRYPTED];
    }
    return strength;
};

export const getEncryptionStrengthFromEncryptionMethod = (method: EncryptionMethod) => {
    const strength = ENCRYPTION_METHOD_STRENGTH[method];
    if (typeof strength == 'undefined') {
        return ENCRYPTION_METHOD_STRENGTH[EncryptionMethod.notEncrypted];
    }
    return strength;
};

export interface IPolicyPbaSettings {
    failedLoginsBeforeLockout: number;
    failedLoginsBeforeErase: number;
    passwordHistory: number;
    passwordMin: number;
    mfa: MFA;
    mfaOptions: MfaOptions;
    enableRememberMe: boolean;
    allowTPM: boolean;
    smartcardLoginEnabled: boolean;
    securityKeyLoginEnabled: boolean;
}

export interface IPolicyFdeSettings {
    failedLoginsBeforeLockout: number;
    failedLoginsBeforeErase: number;
    passwordHistory: number;
    passwordMin: number;
    enableRememberMe: boolean;
}

export enum MFA {
    NotRequired = 0,
    PW = 1,
    CAC = 3,
    TPM = 7,
    FIDO = 9,
    USB_TOKEN = 14,
}

export enum MfaOptions {
    None = 0,
    PIN = 3,
    TOUCH = 4,
}

export interface ILicensesUsedResult {
    sub_id: string;
    plan_id: string;
    avail: number;
    dd_used: number;
    pba_used: number;
}

export type LicensesUsed = {[msp: string]: {[cust: string]: ILicensesUsedResult[]}};
